import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import EasyEstimateHome from '../components/easyEstimate/EasyEstimateHome';
import AddOfferStandaloneForm from '../components/offers/AddOfferStandaloneForm';
import { useNotification } from '../components/layouts/Layout';
import type { OfferStandalone } from '../services/offerStandaloneService';

const EasyEstimatePage: React.FC = () => {
  const isMobile = useIsMobile(768);
  const { showNotification } = useNotification();
  const [openAddForm, setOpenAddForm] = React.useState(false);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);

  const handleAddOffer = (_offer: OfferStandalone) => {
    setOpenAddForm(false);
    setListRefreshKey((k) => k + 1);
    showNotification('Oferta została dodana pomyślnie', 'success');
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
      {/* Nawigacja jak na stronie szczegółów oferty (OfferDetailPage). */}
      <Box
        component={RouterLink}
        to="/offers"
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none',
          textDecoration: 'none',
          color: 'inherit',
        }}
        aria-label="Wróć do ofert"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: '#1F1FB0',
          }}
        >
          Wróć do ofert
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: isMobile ? '20px' : '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px',
          }}
        >
          EasyEstimate
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Tooltip title="Pozycje zatwierdzone (verified) we wszystkich kosztorysach — jedna tabela.">
            <span>
              <IconButton
                component={RouterLink}
                to="/app/easy-estimate/zweryfikowane"
                aria-label="Otwórz pozycje zweryfikowane ze wszystkich kosztorysów"
                sx={{
                  width: 40,
                  height: 40,
                  p: 0.5,
                  color: '#2e7d32',
                  '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.08)' },
                }}
              >
                <Box
                  component="span"
                  className="material-symbols-outlined"
                  sx={{
                    fontFamily: '"Material Symbols Outlined"',
                    fontSize: 22,
                    width: 22,
                    height: 22,
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2e7d32',
                    fontVariationSettings: '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 22',
                    WebkitFontSmoothing: 'antialiased',
                    userSelect: 'none',
                  }}
                  aria-hidden
                >
                  verified
                </Box>
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => setOpenAddForm(true)}
            data-action="add"
            data-hotkey-add="true"
            sx={{
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' },
            }}
          >
            Dodaj ofertę
          </Button>
        </Box>
      </Box>

      <EasyEstimateHome listRefreshKey={listRefreshKey} />

      <AddOfferStandaloneForm open={openAddForm} onClose={() => setOpenAddForm(false)} onAdd={handleAddOffer} />
    </Box>
  );
};

export default EasyEstimatePage;
